import { Box, Flex, Show, Spinner, Text } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import ProfileMenu from '../../ui/ProfileMenu'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { LayoutContext } from '../../ui/Layout/Layout'
import { NavbarFilterGroup } from './NavbarFilterGroup'
import { NavbarButtons } from './NavbarButtons'
import { NavbarSectionTitle } from './NavbarSectionTitle'

import { NavbarSwitchChartViewMode } from './NavbarSwitchChartViewMode'
import { ReportContext } from '../../../contexts/ReportContext'
import { NavbarContext } from '../../../contexts/NavbarContext'
import rootStore from '../../../store/RootStore'
import { observer } from 'mobx-react'

const Navbar = props => {
  const { refScrollContainer } = useContext(ReportContext)

  const [isScrolled, setIsScrolled] = useState(false)

  const handleScrollView = useCallback(() => {
    const isScrolledNow = refScrollContainer?.current?.scrollTop > 100
    setIsScrolled(isScrolledNow)

    // Update CSS variable for navbar height
    document.documentElement.style.setProperty(
      '--navbar-height',
      '180px'
    )
  }, [refScrollContainer])

  useEffect(() => {
    const scrollView = refScrollContainer?.current
    if (scrollView) {
      scrollView.addEventListener('scroll', handleScrollView)

      // Initialize the CSS variable
      document.documentElement.style.setProperty('--navbar-height', '180px')
    }

    return () => {
      if (scrollView) {
        scrollView.removeEventListener('scroll', handleScrollView)
      }
    }
  }, [handleScrollView, refScrollContainer])

  return (
    <NavbarContext.Provider
      value={{
        hideItems: props.hideItems ?? [],
      }}>
      <Container small={isScrolled}>
        <Show above="lg">
          <NavbarDesktop
            {...props}
            isScrolled={isScrolled}
          />
        </Show>
        <Show below="lg">
          <NavbarMobile />
        </Show>
      </Container>
    </NavbarContext.Provider>
  )
}

export const NavbarHidableItems = {
  SearchButton: 'SearchButton',
  ViewModeSwitch: 'ViewModeSwitch',
  NavbarFilterGroup: 'NavbarFilterGroup',
}

/**
 * Will work to sizes >= 1280
 * @param isScrolled
 * @returns {JSX.Element}
 * @constructor
 */
const NavbarDesktop = ({ isScrolled }) => {
  const { hideItems } = useContext(NavbarContext)
  const {
    planStore: { currentPlan },
  } = rootStore

  return (
    <>
      <Flex
        justifyContent="flex-start"
        alignItems={'center'}>
        <Box display="block">
          <NavbarTitle />
        </Box>

        <Box
          display="none"
          alignItems="center"
          className="show-only-if-scroll">
          <NavbarSectionTitle
            fontSize={{
              lg: '14px',
              '2xl': '20px',
            }}
            mb="-10px"
          />

          <Box
            ml={{ lg: '-8px', '2xl': '16px' }}
            mt="4px">
            <NavbarFilterGroup />
          </Box>
        </Box>
        <Box
          mt="12px"
          ml="auto"
          display="none"
          p="0 16px">
          <NavbarSwitchChartViewMode />
        </Box>
        <NavbarButtons
          ml="auto"
        />

        <Box
          ml="auto"
          display="block">
          <ProfileMenu client={currentPlan.planName} />
        </Box>
      </Flex>
      <Flex
        flexDir="row"
        alignItems="center"
        justifyContent="space-between"
        mt={0}
        display="flex">
        <Box ml="-8px">
          <NavbarFilterGroup />
        </Box>
        {!hideItems.includes(NavbarHidableItems.ViewModeSwitch) && (
          <Box
            display="flex"
            p="0 16px">
            <NavbarSwitchChartViewMode />
          </Box>
        )}
      </Flex>
    </>
  )
}

/**
 * Will work to sizes < 1280
 * @returns {JSX.Element}
 * @constructor
 */
const NavbarMobile = () => {
  return (
    <>
      <Flex
        flexDir="row"
        justifyContent="space-between">
        <Flex flexDir="column">
          <Box>
            <NavbarTitle />
            <NavbarSectionTitle
              color="brand.textDarkblue"
              fontSize={{
                base: '16px',
                md: '21px',
              }}
              m={{
                base: '12px 0 6px',
                md: '16px 0 0',
              }}
            />
          </Box>
          <Flex ml="-8px">
            <NavbarFilterGroup />
          </Flex>
        </Flex>
        <Box ml="auto">
          <NavbarButtons small={true} />
        </Box>
      </Flex>
    </>
  )
}

const Container = ({ small, children }) => {
  const { isLeftPanelOpen } = useContext(LayoutContext)
  const headerRef = useRef(null)

  return (
    <>
      <Box
        right={0}
        backgroundColor="white"
        position="absolute"
        zIndex={2}
        overflow={{
          lg: 'hidden',
        }}
        transition=".3s height, .3s padding"
        boxShadow="0 0 10px rgba(0, 0, 0, .1)"
        p={{
          base: '24px 24px 18px 32px',
          sm: '32px 24px 18px 32px',
          lg: '24px 32px',
        }}
        h={{
          base: 'auto',
          lg: '180px',
        }}
        left={{
          base: 0,
          lg: 0,
        }}
        width="100%"
        ref={headerRef}>
        {children}
      </Box>
      {/* Spacer no longer needed with new layout */}
    </>
  )
}

const NavbarTitle = observer(() => {
  const {
    reportStore: { currentReport, loadingReport },
  } = rootStore

  return (
    <Text
      textStyle="heading"
      alignItems={{ base: 'center', sm: 'left' }}
      fontSize={{ base: '20px', lg: '24px', xl: '26px' }}
      textOverflow={'ellipsis'}
      overflow={'hidden'}>
      <Text
        as="span"
        whiteSpace={'nowrap'}>
        {currentReport.name}
      </Text>
    </Text>
  )
})

Navbar.displayName = 'Navbar'

Navbar.propTypes = {
  title: PropTypes.string,
}

export default Navbar
